import axios from 'axios';

// API base configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'wien.mczernecki.pl' ? '/api' : 'http://localhost:8080/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 10000, // 10 second timeout
});

// Request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    // Add authentication header if user is logged in
    const storedUser = localStorage.getItem('vienna_user');
    
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        
        if (user && user.id) {
          const authToken = `Bearer user:${user.id}`;
          
          // Use standard Authorization header
          config.headers.Authorization = authToken;
          
          // Fallback: Include in request body for PUT/POST/DELETE (for problematic hosting)
          if (['PUT', 'POST', 'DELETE'].includes(config.method?.toUpperCase())) {
            if (config.data && typeof config.data === 'object') {
              config.data = { ...config.data, __auth_token: authToken };
            } else if (!config.data) {
              config.data = { __auth_token: authToken };
            }
          }
        }
      } catch (error) {
        console.error('Error parsing stored user for auth:', error);
        localStorage.removeItem('vienna_user');
      }
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    console.error('Failed URL:', error.config?.url);
    console.error('Request headers:', error.config?.headers);
    
    // Handle authentication errors with retry logic
    if (error.response?.status === 401) {
      console.warn('Authentication failed for:', error.config?.url);
      console.warn('User in localStorage:', localStorage.getItem('vienna_user'));
      console.warn('Authorization header sent:', error.config?.headers?.Authorization);
      
      // Don't immediately redirect if this is the first attempt and we have a user in localStorage
      const storedUser = localStorage.getItem('vienna_user');
      if (storedUser && !error.config.__isRetryRequest) {
        console.warn('Retrying request with fresh auth header...');
        
        // Mark this as a retry to prevent infinite loops
        error.config.__isRetryRequest = true;
        
        // Refresh the authorization header
        try {
          const user = JSON.parse(storedUser);
          if (user && user.id) {
            const authToken = `Bearer user:${user.id}`;
            error.config.headers.Authorization = authToken;
            error.config.headers['X-Authorization'] = authToken;
            return api.request(error.config);
          }
        } catch (parseError) {
          console.error('Error parsing stored user for retry:', parseError);
        }
      }
      
      // If retry failed or no stored user, redirect to login
      console.warn('Authentication permanently failed - redirecting to login');
      localStorage.removeItem('vienna_user');
      
      // Don't redirect if we're already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    
    // Handle 403 errors specifically for better user feedback
    if (error.response?.status === 403) {
      console.error('403 Forbidden Error - Possible auth issue');
      const errorMessage = error.response?.data?.message || 'Access denied';
      
      // If it's an auth-related 403, try to re-authenticate
      if (errorMessage.includes('Access denied') || errorMessage.includes('only modify your own')) {
        console.warn('403 might be due to authentication issues - user may need to log in again');
        
        // Show user-friendly error
        error.userMessage = 'Your session may have expired. Please refresh the page and log in again.';
      }
    }
    
    // Handle CORS errors specifically
    if (error.message.includes('Network Error') || !error.response) {
      console.error('CORS or Network Error - Check if backend is running');
      error.userMessage = 'Cannot connect to server. Please ensure the backend is running.';
    }
    
    return Promise.reject(error);
  }
);

// API Service Object
const apiService = {
  // Health Check
  healthCheck: () => api.get('/'),

  // Authentication
  auth: {
    login: (credentials) => api.post('/users/login', credentials),
    register: (userData) => api.post('/users/register', userData),
  },

  // Users
  users: {
    getAll: () => api.get('/users'),
    update: (userData) => api.post('/users', { ...userData, _method: 'PUT' }),
    delete: (userId) => api.post('/users', { id: userId, _method: 'DELETE' }),
  },

  // Currencies
  currencies: {
    getAll: () => api.get('/currencies'),
    create: (currencyData) => api.post('/currencies', currencyData),
    update: (currencyData) => api.post('/currencies', { ...currencyData, _method: 'PUT' }),
    delete: (currencyId) => api.post('/currencies', { id: currencyId, _method: 'DELETE' }),
  },

  // Income (Budget)
  income: {
    getAll: () => api.get('/income'),
    create: (incomeData) => api.post('/income', incomeData),
    update: (incomeData) => api.post('/income', { ...incomeData, _method: 'PUT' }),
    delete: (incomeId) => api.post('/income', { id: incomeId, _method: 'DELETE' }),
  },

  // Expenses (Outcome)
  expenses: {
    getAll: () => api.get('/outcome'),
    create: (expenseData) => api.post('/outcome', expenseData),
    update: (expenseData) => api.post('/outcome', { ...expenseData, _method: 'PUT' }),
    delete: (expenseId) => api.post('/outcome', { id: expenseId, _method: 'DELETE' }),
  },
};

export default apiService; 